import { useState, useContext, useEffect, useCallback } from "react";
import { Grid } from "@mui/material";
import CashierMismatchDialog from "components/popups/CashierMismatchDialog/CashierMismatchDialog";
import CloseShiftDialog from "components/popups/CloseShiftDialog/CloseShiftDialog";
import Loader from "../../utils/Loading";
import styles from "./styles.module.css";
import { useNavigate } from "react-router-dom";
import { ROUTES, KEYBOARD_KEYS, EVENTS } from "const";
import { eventsEmitter } from "utils/events";
import toast from "react-hot-toast";
import ClearIcon from "images/svg/back.svg";
import SalesPersonContext from "context/SalesPersonContext";
import * as ShiftHelper from "helpers/ShiftHelper";
import { navigateToNextPosStep } from "helpers/NavigationHelper";

function LockPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [isInvalidPin, setIsInvalidPin] = useState(false);
  const [mismatchDialogOpen, setMismatchDialogOpen] = useState(false);
  const [mismatchCashierName, setMismatchCashierName] = useState("");
  const [closeShiftDialogOpen, setCloseShiftDialogOpen] = useState(false);

  const navigate = useNavigate();

  const { findSalesPersonByPassword, setCurrentSalesperson } = useContext(SalesPersonContext);

  const handleInvalidPin = useCallback(() => {
    setIsInvalidPin(true);
    setTimeout(() => {
      setIsInvalidPin(false);
      setPassword("");
    }, 350);
  }, []);

  const handleLoginWithPin = useCallback(
    async (pin) => {
      if (!pin || pin.length < 4 || isLoading) return;

      setIsLoading(true);
      try {
        const matched = await findSalesPersonByPassword(pin);

        if (!matched) {
          toast.error("Invalid PIN");
          handleInvalidPin();
          return;
        }

        // Check active shift cashier mismatch (identical to sanoft-pos-android-app)
        const currentShift = ShiftHelper.getCurrentActiveShift();
        if (currentShift && !ShiftHelper.isCurrentShiftCashierSame(currentShift, matched)) {
          setMismatchCashierName(currentShift.cashier?.name || currentShift.cashier?.username || "another cashier");
          setMismatchDialogOpen(true);
          return;
        }

        setCurrentSalesperson(matched);
        toast.success(`Welcome, ${matched.name || matched.username || "Salesperson"}`);

        await navigateToNextPosStep(navigate, { replace: true });
      } catch (error) {
        console.error("Salesperson login failed:", error);
        toast.error("Login failed. Please try again.");
        handleInvalidPin();
      } finally {
        setIsLoading(false);
      }
    },
    [findSalesPersonByPassword, setCurrentSalesperson, navigate, handleInvalidPin, isLoading],
  );

  // Auto-submit when 4 digits entered (debounced with cleanup to prevent double execution)
  useEffect(() => {
    if (password.length === 4 && !isLoading && !isInvalidPin) {
      const timer = setTimeout(() => {
        handleLoginWithPin(password);
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [password, handleLoginWithPin, isLoading, isInvalidPin]);

  const handleBtnClick = useCallback(
    (char) => {
      if (isLoading) return;
      setPassword((prev) => (prev.length < 4 ? prev + char : prev));
    },
    [isLoading],
  );

  const handleBtnClear = useCallback(() => {
    if (isLoading) return;
    setPassword((prev) => prev.slice(0, -1));
  }, [isLoading]);

  const handleBtnClearAll = useCallback(() => {
    if (isLoading) return;
    setPassword("");
  }, [isLoading]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept if dialog is open
      if (mismatchDialogOpen || closeShiftDialogOpen || isLoading) return;

      if (e.key >= "0" && e.key <= "9") {
        e.preventDefault();
        handleBtnClick(e.key);
      } else if (e.key === KEYBOARD_KEYS.BACKSPACE) {
        e.preventDefault();
        handleBtnClear();
      } else if (e.key === KEYBOARD_KEYS.ENTER) {
        e.preventDefault();
        if (password.length === 4) {
          handleLoginWithPin(password);
        }
      } else if (e.key === KEYBOARD_KEYS.ESCAPE) {
        e.preventDefault();
        setPassword("");
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [
    handleBtnClick,
    handleBtnClear,
    handleLoginWithPin,
    password,
    mismatchDialogOpen,
    closeShiftDialogOpen,
    isLoading,
  ]);

  const handleMismatchClose = () => {
    setMismatchDialogOpen(false);
    setPassword("");
  };

  const handleMismatchCloseShift = () => {
    setMismatchDialogOpen(false);
    setPassword("");
    setCloseShiftDialogOpen(true);
  };

  const handleCloseShiftDialogClose = () => {
    setCloseShiftDialogOpen(false);
    setPassword("");
  };

  const handleCloseShiftSubmit = () => {
    setCloseShiftDialogOpen(false);
    setPassword("");
  };

  const isPasswordEntered = (position) => password.length >= position;

  return (
    <>
      <Loader isOpen={isLoading} />
      <Grid container className={styles.textArea}>
        <Grid item xs={12}>
          <Grid container rowSpacing={1}>
            <Grid item xs={12} display="flex" justifyContent={"center"}>
              <span className={styles.head1}>Sanoft</span> <span className={styles.head2}>POS</span>
            </Grid>
            <Grid item xs={12} className={styles.text}>
              <span>
                Enter your personal PIN to quickly and securely unlock your account and pick up where you left off
              </span>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent={"center"}
              margin={"22px"}
              className={isInvalidPin ? styles.shake : ""}
            >
              <Grid container width={"280px"}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={3} key={i} className={styles.btnConteiner}>
                    <Grid className={styles.pwdButton}>
                      <Grid className={`${isPasswordEntered(i) && styles.pwdActive}`}></Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent={"center"}>
              <Grid className={styles.keypad}>
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((n) => (
                  <button
                    key={n}
                    className={`${styles.key} ${n === "1" ? styles.topLeft : ""} ${n === "3" ? styles.topRight : ""}`}
                    onClick={() => handleBtnClick(n)}
                  >
                    {n}
                  </button>
                ))}

                <button className={`${styles.keySmall} ${styles.bottomLeft}`} onClick={handleBtnClear}>
                  <img src={ClearIcon} alt="clear" />
                </button>

                <button className={styles.key} onClick={() => handleBtnClick("0")}>
                  0
                </button>

                <button className={`${styles.clearBtn} ${styles.bottomRight}`} onClick={handleBtnClearAll}>
                  Clear
                </button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Cashier Mismatch Alert Dialog */}
      <CashierMismatchDialog
        open={mismatchDialogOpen}
        onClose={handleMismatchClose}
        onCloseShift={handleMismatchCloseShift}
        cashierName={mismatchCashierName}
      />

      {/* Close Shift Dialog */}
      <CloseShiftDialog
        open={closeShiftDialogOpen}
        onClose={handleCloseShiftDialogClose}
        onSubmit={handleCloseShiftSubmit}
        redirectRoute={ROUTES.LOCK_SCREEN}
      />
    </>
  );
}

export default LockPage;
